import React, { useState, useEffect } from "react";

const EmailVerification = ({
  apiUrl,
  onVerified, // callback when email is verified
  domain = "@csscdr.gouv.qc.ca", // default domain, can be customized
  error,
}) => {
  const [emailToVerify, setEmailToVerify] = useState("");
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationError, setEmailVerificationError] = useState("");
  const [codeVerificationError, setCodeVerificationError] = useState("");
  const [sendingVerificationCode, setSendingVerificationCode] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const resendTimeout = 90; // seconds

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  useEffect(() => {
    if (emailVerified && onVerified) {
      onVerified(emailToVerify);
    }
  }, [emailVerified, emailToVerify, onVerified]);

  const handleEmailToVerifyChange = (e) => {
    setEmailToVerify(e.target.value);
    setEmailVerificationError("");
  };

  const handleSendVerificationCode = async () => {
    setEmailVerificationError("");
    setCodeVerificationError("");
    if (domain && !emailToVerify.endsWith(domain)) {
      setEmailVerificationError(
        "Veuillez utiliser votre courriel du Centre de services scolaire.",
      );
      return;
    }
    setSendingVerificationCode(true);
    try {
      const res = await fetch(`${apiUrl}/document/send-verification-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailToVerify }),
      });
      const data = await res.json();
      if (res.ok) {
        setEmailVerificationSent(true);
        setResendTimer(resendTimeout);
      } else {
        setEmailVerificationError(
          data.error || "Erreur lors de l'envoi du code.",
        );
      }
    } catch (err) {
      setEmailVerificationError("Erreur réseau.");
    } finally {
      setSendingVerificationCode(false);
    }
  };

  const handleVerificationCodeChange = (e) => {
    setVerificationCode(e.target.value);
    setCodeVerificationError("");
  };

  const handleVerifyCode = async () => {
    setCodeVerificationError("");
    try {
      const res = await fetch(`${apiUrl}/document/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailToVerify, code: verificationCode }),
      });
      const data = await res.json();
      if (res.ok && data.verified) {
        setEmailVerified(true);
        setCodeVerificationError("");
      } else {
        setCodeVerificationError(data.error || "Code invalide.");
      }
    } catch (err) {
      setCodeVerificationError("Erreur réseau.");
    }
  };

  const inputBase = {
    backgroundColor: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "0.5rem",
    padding: "0.75rem",
    color: "#ffffff",
    outline: "none",
    fontSize: "0.9rem",
    fontFamily: "Inter, sans-serif",
  };

  return (
    <div
      style={{
        backgroundColor: "#00447f",
        borderRadius: "1rem",
        padding: "2rem",
        boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          marginBottom: "1.5rem",
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{
            color: "#ffffff",
            fontSize: 24,
            display: "inline-block",
            lineHeight: 1,
          }}
        >
          verified_user
        </span>
        <h2
          style={{
            fontFamily: "Public Sans, sans-serif",
            fontWeight: 700,
            fontSize: "1.25rem",
            color: "#ffffff",
            margin: 0,
          }}
        >
          Dernière étape : Vérification de l'identité
        </h2>
      </div>

      {emailVerified ? (
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.15)",
            borderRadius: "0.75rem",
            padding: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{ color: "#fae51f", display: "inline-block", lineHeight: 1 }}
          >
            check_circle
          </span>
          <span style={{ color: "#ffffff", fontWeight: 600 }}>
            Courriel vérifié avec succès !
          </span>
        </div>
      ) : (
        <div
          style={{
            maxWidth: "28rem",
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
          }}
        >
          {/* Email row */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <label
              htmlFor="emailToVerify"
              style={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "rgba(255,255,255,0.9)",
              }}
            >
              Courriel professionnel (Centre de services){" "}
              {domain ? ` (${domain})` : ""}
            </label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                type="email"
                id="emailToVerify"
                value={emailToVerify}
                onChange={handleEmailToVerifyChange}
                disabled={emailVerificationSent}
                placeholder={`prenom.nom${domain || "@csscdr.gouv.qc.ca"}`}
                style={{
                  ...inputBase,
                  flex: 1,
                  border: emailVerificationError
                    ? "1px solid #fca5a5"
                    : "1px solid rgba(255,255,255,0.2)",
                }}
              />
              <button
                type="button"
                onClick={handleSendVerificationCode}
                disabled={
                  !emailToVerify ||
                  emailVerificationSent ||
                  sendingVerificationCode
                }
                style={{
                  backgroundColor: "#ffffff",
                  color: "#00447f",
                  border: "none",
                  borderRadius: "0.5rem",
                  padding: "0.75rem 1.25rem",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  fontFamily: "Inter, sans-serif",
                  cursor:
                    !emailToVerify ||
                    emailVerificationSent ||
                    sendingVerificationCode
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    !emailToVerify ||
                    emailVerificationSent ||
                    sendingVerificationCode
                      ? 0.6
                      : 1,
                  whiteSpace: "nowrap",
                  transition: "background-color 0.15s",
                }}
              >
                {sendingVerificationCode ? (
                  <img
                    src="/emailVerificationLoading.gif"
                    alt="Chargement..."
                    style={{ height: 20, verticalAlign: "middle" }}
                  />
                ) : (
                  "Envoyer"
                )}
              </button>
            </div>
            {emailVerificationError && (
              <p style={{ color: "#fca5a5", fontSize: "0.8rem", margin: 0 }}>
                {emailVerificationError}
              </p>
            )}
            {emailVerificationSent && resendTimer > 0 && (
              <p
                style={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: "0.8rem",
                  margin: 0,
                }}
              >
                Code envoyé ! Vous pouvez demander un nouveau code dans{" "}
                {resendTimer} secondes.
              </p>
            )}
            {emailVerificationSent && resendTimer === 0 && (
              <button
                type="button"
                onClick={handleSendVerificationCode}
                disabled={sendingVerificationCode}
                style={{
                  background: "none",
                  border: "none",
                  color: "#fae51f",
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                  textAlign: "left",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Renvoyer le code
              </button>
            )}
          </div>

          {/* Code verification row */}
          {emailVerificationSent && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <label
                htmlFor="verificationCode"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                Code de vérification reçu :
              </label>
              <div className="flex flex-col md:flex-row" style={{ gap: "1rem" }}>
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={handleVerificationCodeChange}
                  maxLength={6}
                  placeholder="000000"
                  style={{
                    ...inputBase,
                    width: "8rem",
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                    textAlign: "center",
                    fontSize: "1rem",
                    border: codeVerificationError
                      ? "1px solid #fca5a5"
                      : "1px solid rgba(255,255,255,0.2)",
                  }}
                />
                <button
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={!verificationCode}
                  style={{
                    backgroundColor: "#fae51f",
                    color: "#201c00",
                    border: "none",
                    borderRadius: "0.5rem",
                    padding: "0.75rem 1.5rem",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    fontFamily: "Inter, sans-serif",
                    cursor: !verificationCode ? "not-allowed" : "pointer",
                    opacity: !verificationCode ? 0.6 : 1,
                    transition: "opacity 0.15s",
                  }}
                >
                  Vérifier et soumettre la demande
                </button>
              </div>
              {codeVerificationError && (
                <p style={{ color: "#fca5a5", fontSize: "0.8rem", margin: 0 }}>
                  {codeVerificationError}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* External validation error from parent form */}
      {error && !emailVerificationError && !emailVerified && (
        <p
          style={{
            color: "#fca5a5",
            fontSize: "0.8rem",
            marginTop: "0.75rem",
            margin: "0.75rem 0 0",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default EmailVerification;
